/**
 * The undersea-cable network, for the global stop.
 *
 * Source: drawn by hand after the public cable maps (and the land backbones
 * between them). The routes are approximate and simplified, one line standing
 * for a bundle of cables, but they run where the real ones run: round Africa,
 * through Suez, across both oceans.
 *
 * Encoding: each route is a list of `[lat, lon]` waypoints in degrees. Waypoints
 * closer than half a degree are one node, so the routes join into one network
 * and data can find its way from Prague to anywhere (`buildCableNetwork`).
 */
import { angleBetween, RAD, unitVector, type Vec3 } from './geo'

/** A `[lat, lon]` pair in degrees. */
export type Waypoint = readonly [number, number]

/** Undersea cables. */
// prettier-ignore
export const SEA_CABLES: readonly (readonly Waypoint[])[] = [
  /* across the North Atlantic */
  [[36.85, -75.98], [37.5, -65], [39, -50], [41.5, -35], [43.5, -20], [44.2, -10], [43.4, -3.0]],          /* MAREA: Virginia – Bilbao */
  [[40.8, -72.9], [41.5, -65], [44, -50], [47.5, -35], [49.5, -20], [50.3, -10], [50.83, -4.55]],          /* TAT-14, Grace Hopper: New York – Bude */
  [[36.85, -75.98], [38, -62], [41, -45], [44, -28], [46, -12], [46.72, -1.95]],                           /* Dunant: Virginia – France */
  [[42.0, -70.5], [43, -60], [46, -45], [48, -30], [48.6, -15], [48.8, -6], [48.73, -3.46]],               /* Apollo: Boston – Lannion */
  [[44.65, -63.6], [45.5, -55], [49, -35], [51, -20], [51.5, -11], [52.0, -6.0], [53.4, -4.8], [53.65, -3.05]], /* Hibernia: Halifax – Southport */
  [[40.2, -74.0], [42, -62], [47, -45], [52, -28], [54.5, -15], [57.5, -8], [59.2, -3], [58.2, 3], [57.0, 7.5], [55.6, 8.1]], /* Havfrue: New Jersey – Denmark */
  [[47.5, -52.7], [52, -50], [58, -48], [60.7, -46.0], [62, -38], [63.4, -20]],                            /* Greenland Connect */
  [[63.4, -20], [62, -10], [60, -3], [58.2, 3]],                                                           /* Danice: Iceland – North Sea */
  /* the South Atlantic and Brazil */
  [[-3.72, -38.5], [3, -33], [12, -27], [17, -25], [25, -19], [32, -14], [37.95, -8.87]],                   /* EllaLink: Fortaleza – Sines */
  [[-3.72, -38.5], [-5, -25], [-6.5, -5], [-8.4, 12], [-8.83, 13.23]],                                     /* SACS: Fortaleza – Luanda */
  [[-3.72, -38.5], [-1.5, -20], [1, 0], [2.94, 9.9]],                                                      /* SAIL: Fortaleza – Kribi */
  [[-23.96, -46.33], [-22, -40], [-13, -36], [-5, -33.5], [2, -40], [10, -50], [18, -60], [25, -66], [30, -74], [28, -79.5], [26.35, -80.08]], /* Monet, Seabras: Santos – Florida */
  [[-3.72, -38.5], [-5.5, -34.8], [-8.05, -34.85], [-13, -38.4], [-20, -39.5], [-22.9, -42.5], [-23.96, -46.33], [-28, -48], [-33, -51.5], [-36.3, -56.6]], /* down the coast to Argentina */
  /* round Africa */
  [[38.44, -9.3], [34, -11], [29, -14], [24, -17], [20, -18], [14.7, -17.7], [11, -17], [7.5, -13.8], [4.8, -8.5], [4.4, -3], [5.5, 1.0], [6.2, 3.4],
   [4, 6.5], [1, 8.5], [-3, 10], [-6, 11.5], [-8.83, 13.23], [-13, 12.2], [-17.5, 11.5], [-22.9, 14.3], [-28.6, 16.0], [-33.9, 18.35]], /* WACS, 2Africa: Portugal – Dakar – Lagos – Cape Town */
  [[-33.9, 18.35], [-35.2, 20.5], [-34.5, 25.5], [-32, 29.5], [-29.85, 31.1], [-25.97, 32.75], [-22, 36], [-17, 40], [-12, 41.5], [-6.8, 39.4],
   [-4.05, 39.75], [0, 43.5], [2.03, 45.4], [6, 50], [11.0, 51.6], [12.3, 47], [11.6, 43.15]],               /* EASSy, 2Africa: Cape Town – Mombasa – Djibouti */
  [[-29.85, 31.1], [-26, 40], [-21, 52], [-20.15, 57.5], [-12, 62], [0, 68], [8, 74], [9.95, 76.25]],      /* SAFE: Durban – Mauritius – Kochi */
  /* Europe to Asia, through Suez */
  [[43.3, 5.37], [40.5, 7], [37.6, 10.5], [35.5, 15], [34, 22], [32.5, 28], [31.3, 32.3], [30.0, 32.55], [27.5, 34], [24, 36.3], [20, 38.5],
   [16, 41], [12.7, 43.3], [12.3, 46], [13.3, 51], [15.5, 56], [19, 58.8], [22, 60.5], [24, 62], [23.5, 66], [19.05, 72.8], [15, 73.3],
   [10.5, 75.5], [7.5, 77.2], [6.0, 79.5], [6.9, 79.85]],                                                  /* SEA-ME-WE, AAE-1: Marseille – Suez – Mumbai – Colombo */
  [[11.6, 43.15], [12.3, 46]],                                                                             /* Djibouti */
  [[24, 62], [24.8, 58.5], [25.12, 56.36]],                                                               /* to the Gulf */
  [[23.5, 66], [24.8, 66.97]],                                                                            /* Karachi */
  [[6.9, 79.85], [5.8, 82], [6, 88], [6.2, 94], [5.8, 97.5], [4.5, 99.5], [2.5, 101.3], [1.25, 103.9]],   /* Colombo – Singapore */
  [[6.9, 79.85], [8.5, 81.5], [13.08, 80.3]],                                                             /* Chennai */
  [[13.08, 80.3], [11, 85], [8, 92], [6.2, 94]],
  [[43.3, 5.37], [41, 8.5], [38.5, 12.0], [38.13, 13.36], [36.5, 16], [35.2, 21.5], [34.5, 25.5], [34, 30], [32.6, 34.6], [32.08, 34.75]], /* across the Mediterranean */
  /* East Asia */
  [[1.25, 103.9], [3, 105.5], [6, 107.5], [10, 110], [14, 111.5], [18, 112.5], [22.2, 114.3]],            /* APG: Singapore – Hong Kong */
  [[22.2, 114.3], [22.5, 117], [23.5, 119.5], [25.17, 121.2], [26.5, 123.5], [28.5, 127], [31, 131.5], [33, 135.5], [34.95, 139.95]], /* – Taiwan – Japan */
  [[22.2, 114.3], [23.5, 117.5], [26, 120.5], [29, 122.5], [31.3, 121.9]],                               /* – Shanghai */
  [[22.2, 114.3], [18, 117], [14.4, 120.6]],                                                              /* – Manila */
  /* Asia to Australia, and round it */
  [[1.25, 103.9], [-1, 105.5], [-3.5, 106.5], [-5.8, 106.2], [-6.4, 105.4], [-8, 104.5], [-14, 108], [-22, 111.8], [-28, 112.8], [-31.95, 115.75]], /* Indigo: Singapore – Jakarta – Perth */
  [[-31.95, 115.75], [-34.5, 115], [-35.3, 118], [-35, 123], [-33.5, 130], [-35.6, 136], [-37.5, 140], [-39.5, 144], [-38.5, 148.5], [-36, 150.5], [-33.87, 151.25]], /* Perth – Sydney */
  [[-33.87, 151.25], [-28, 156], [-18, 157], [-10, 155.5], [-4, 155.5], [1, 152], [7, 147.5], [13.45, 144.75]], /* JGA: Sydney – Guam */
  [[13.45, 144.75], [20, 142], [27, 140.5], [32, 140], [34.95, 139.95]],                                  /* Guam – Japan */
  [[13.45, 144.75], [15, 133], [15.76, 121.6]],                                                           /* Guam – Philippines */
  [[-33.87, 151.25], [-35.5, 160], [-37.3, 170], [-37.8, 174.8]],                                         /* across the Tasman */
  /* across the Pacific */
  [[34.95, 139.95], [36, 147], [40, 160], [43, 175], [45, -170], [46, -155], [45.5, -140], [44, -130], [43.1, -124.45]], /* FASTER, Jupiter: Japan – Oregon */
  [[34.95, 139.95], [33.5, 150], [32.5, 165], [32, 180], [32, -165], [32.5, -150], [33.5, -135], [33.95, -118.45]], /* Unity, PLCN: Japan – Los Angeles */
  [[13.45, 144.75], [17, 160], [20, 175], [21.3, -158], [25, -145], [30, -130], [33.95, -118.45]],         /* SEA-US, AAG: Guam – Hawaii – LA */
  [[-33.87, 151.25], [-30, 160], [-24, 170], [-18.15, 178.4], [-10, -175], [0, -168], [10, -162], [21.3, -158], [27, -145], [32, -130], [35.3, -120.85]], /* Southern Cross: Sydney – Fiji – Hawaii – California */
  [[-37.8, 174.8], [-33, 175], [-25, 177], [-18.15, 178.4]],                                               /* New Zealand – Fiji */
  [[33.95, -118.45], [28, -118], [18, -108], [5, -95], [-8, -85], [-20, -77], [-30, -73], [-33.04, -71.65]], /* Curie: LA – Chile */
  [[8.6, -79.5], [5, -80.5], [0, -81.8], [-5, -82], [-12.05, -77.25], [-18, -71.5], [-23.6, -70.6], [-33.04, -71.65]], /* Panama – Lima – Valparaíso */
  /* the Caribbean and the Gulf of Mexico */
  [[26.35, -80.08], [25.5, -79.2], [23.5, -75.5], [21.5, -71], [19.5, -67.5], [18.47, -66.1]],            /* Florida – Puerto Rico */
  [[18.47, -66.1], [15, -66], [11.5, -66.8], [10.6, -67.0], [12.2, -70.5], [11.6, -73.5], [11.0, -74.8], [10.5, -77], [9.36, -79.9]], /* – Venezuela – Colombia – Panama */
  [[25.7, -80.1], [24.4, -81.5], [23.5, -84], [21.5, -86.5], [21.16, -86.82]],                            /* Florida – Cancún */
  [[21.16, -86.82], [21.6, -87.2], [22.5, -90], [21, -94], [19.2, -96.13]],                               /* – Veracruz */
  /* the North Sea and the Baltic */
  [[52.95, 1.6], [52.6, 3], [52.45, 4.6]],
  [[55.6, 12.6], [55.0, 14.5], [57, 19], [59.4, 22.5], [60.15, 24.9]]
]

/** Land backbones. */
// prettier-ignore
export const LAND_BACKBONES: readonly (readonly Waypoint[])[] = [
  /* Europe, from Prague */
  [[50.08, 14.44], [50.11, 8.68], [48.86, 2.35], [51.51, -0.13]],                                         /* Prague – Frankfurt – Paris – London */
  [[50.11, 8.68], [52.37, 4.9], [52.45, 4.6]],                                                             /* – Amsterdam */
  [[50.08, 14.44], [48.21, 16.37], [47.5, 19.04], [44.8, 20.46], [42.7, 23.32], [41.0, 28.97]],          /* – Vienna – Budapest – Istanbul */
  [[50.08, 14.44], [52.52, 13.4], [55.68, 12.57], [55.6, 12.6]],                                          /* – Berlin – Copenhagen */
  [[55.68, 12.57], [55.6, 8.1]],
  [[50.08, 14.44], [52.23, 21.01], [53.9, 27.57], [55.75, 37.62]],                                        /* – Warsaw – Minsk – Moscow */
  [[55.75, 37.62], [56.84, 60.6], [55.0, 82.9], [52.3, 104.3], [39.9, 116.4]],                           /* – Siberia – Beijing (TEA) */
  [[39.9, 116.4], [31.3, 121.9]], [[31.3, 121.9], [22.2, 114.3]],                                         /* China */
  [[55.0, 82.9], [43.24, 76.9], [42.87, 74.57]],                                                          /* – Almaty – Bishkek */
  [[50.11, 8.68], [45.46, 9.19], [43.3, 5.37]],                                                           /* Frankfurt – Milan – Marseille */
  [[48.86, 2.35], [48.73, -3.46]], [[48.86, 2.35], [46.72, -1.95]],                                       /* Paris – the Atlantic landings */
  [[48.86, 2.35], [40.42, -3.7], [43.4, -3.0]], [[40.42, -3.7], [38.72, -9.14], [38.44, -9.3]], [[38.72, -9.14], [37.95, -8.87]], /* Madrid – Bilbao, Lisbon */
  [[51.51, -0.13], [50.83, -4.55]], [[51.51, -0.13], [53.65, -3.05]], [[51.51, -0.13], [52.95, 1.6]],     /* London – the landings */
  /* North America */
  [[40.71, -74.0], [40.8, -72.9]], [[40.71, -74.0], [40.2, -74.0]], [[40.71, -74.0], [38.9, -77.04], [36.85, -75.98]], /* New York – the landings, Washington */
  [[40.71, -74.0], [42.36, -71.06], [42.0, -70.5]], [[42.36, -71.06], [45.5, -73.57], [44.65, -63.6]],    /* – Boston – Montréal – Halifax */
  [[40.71, -74.0], [41.88, -87.63], [39.74, -104.99], [34.05, -118.24], [33.95, -118.45]],               /* – Chicago – Denver – Los Angeles */
  [[41.88, -87.63], [47.6, -122.33], [43.1, -124.45]], [[34.05, -118.24], [35.3, -120.85]],               /* – Seattle – Oregon */
  [[41.88, -87.63], [32.78, -96.8], [29.76, -95.37], [25.76, -80.19], [26.35, -80.08]], [[25.76, -80.19], [25.7, -80.1]], /* – Dallas – Miami */
  [[32.78, -96.8], [25.67, -100.31], [19.43, -99.13], [19.2, -96.13]],                                    /* – Monterrey – Mexico City – Veracruz */
  /* South America, Africa, Asia */
  [[-23.55, -46.63], [-23.96, -46.33]], [[-23.55, -46.63], [-3.72, -38.5]],                               /* São Paulo – Fortaleza */
  [[30.04, 31.24], [31.3, 32.3]],                                                                          /* Cairo */
  [[19.05, 72.8], [28.6, 77.2], [13.08, 80.3]],                                                           /* Mumbai – Delhi – Chennai */
  [[35.68, 139.77], [34.95, 139.95]], [[-33.92, 18.42], [-33.9, 18.35]], [[9.36, -79.9], [8.6, -79.5]]   /* Tokyo, Cape Town, across Panama */
]

/** The places on the page that data from Prague goes to. */
// prettier-ignore
export const DESTINATIONS: readonly Waypoint[] = [[19.2, -96.13], [14.7, -17.7], [42.87, 74.57], [40.71, -74.0], [34.05, -118.24], [-23.55, -46.63],
             [35.68, 139.77], [1.25, 103.9], [-33.87, 151.25], [-33.92, 18.42], [19.05, 72.8], [22.2, 114.3],
             [6.2, 3.4], [25.12, 56.36], [-33.04, -71.65], [55.75, 37.62], [39.9, 116.4], [-4.05, 39.75]]

export const PRAGUE: Waypoint = [50.08, 14.44]

/** Waypoints closer than this (radians) are one node. */
const MERGE_ANGLE = 0.5 * RAD
/** Spacing of the points along a route. */
const STEP = 1.2 * RAD

/** A landing or junction. */
export interface CableNode {
  readonly point: Vec3
  /** The stretches of cable that leave this node. */
  readonly adjacent: { to: number; length: number; route: number; from: number; end: number }[]
  /** How many routes pass through this node. Written by the builder as it adds routes. */
  degree: number
}

/** One route, sampled along the great circles between its waypoints. */
export interface CableRoute {
  /** True for a land backbone, false for an undersea cable. */
  readonly land: boolean
  readonly x: number[]
  readonly y: number[]
  readonly z: number[]
  /** Distance (radians) from the route's start to each sample. */
  readonly cumulative: number[]
  /** The route's waypoints: the node each is, and its sample index. */
  readonly waypoints: { node: number; sample: number }[]
  /** Written by the builder once the route is sampled. */
  length: number
}

export interface CableNetwork {
  readonly nodes: CableNode[]
  readonly routes: CableRoute[]
  /** The node for a waypoint, added if no node is within the merge angle. */
  node(waypoint: Waypoint): number
  /** Prague's node. */
  readonly start: number
  /** Distance (radians) from Prague along the network to each node, `Infinity` if unreachable. */
  readonly distance: number[]
}

export function buildCableNetwork(
  seaRoutes: readonly (readonly Waypoint[])[] = SEA_CABLES,
  landRoutes: readonly (readonly Waypoint[])[] = LAND_BACKBONES
): CableNetwork {
  const nodes: CableNode[] = []
  const routes: CableRoute[] = []
  const mergeDot = Math.cos(MERGE_ANGLE)

  function toVector([lat, lon]: Waypoint): Vec3 {
    return unitVector(lat * RAD, lon * RAD)
  }

  function node(waypoint: Waypoint): number {
    const point = toVector(waypoint)
    const near = nodes.findIndex(
      (n) => n.point[0] * point[0] + n.point[1] * point[1] + n.point[2] * point[2] > mergeDot
    )
    if (near >= 0) return near
    nodes.push({ point, adjacent: [], degree: 0 })
    return nodes.length - 1
  }

  function addRoute(waypoints: readonly Waypoint[], land: boolean) {
    const route: CableRoute = {
      land,
      x: [],
      y: [],
      z: [],
      cumulative: [],
      waypoints: [],
      length: 0,
    }
    let travelled = 0
    for (let k = 0; k < waypoints.length; k++) {
      const n = node(waypoints[k])
      nodes[n].degree++
      route.waypoints.push({ node: n, sample: route.x.length })
      const a = nodes[n].point
      if (k === waypoints.length - 1) {
        route.x.push(a[0])
        route.y.push(a[1])
        route.z.push(a[2])
        route.cumulative.push(travelled)
        break
      }
      const b = toVector(waypoints[k + 1])
      const angle = angleBetween(a, b)
      const steps = Math.max(1, Math.ceil(angle / STEP))
      const sinAngle = Math.sin(angle) || 1
      for (let j = 0; j < steps; j++) {
        const u = j / steps
        const wa = angle > 1e-6 ? Math.sin((1 - u) * angle) / sinAngle : 1 - u
        const wb = angle > 1e-6 ? Math.sin(u * angle) / sinAngle : u
        route.x.push(a[0] * wa + b[0] * wb)
        route.y.push(a[1] * wa + b[1] * wb)
        route.z.push(a[2] * wa + b[2] * wb)
        route.cumulative.push(travelled + angle * u)
      }
      travelled += angle
    }
    route.length = travelled
    // the network's edges: from waypoint to waypoint, along this route
    for (let k = 0; k + 1 < route.waypoints.length; k++) {
      const a = route.waypoints[k]
      const b = route.waypoints[k + 1]
      const length = route.cumulative[b.sample] - route.cumulative[a.sample]
      const index = routes.length
      nodes[a.node].adjacent.push({
        to: b.node,
        length,
        route: index,
        from: a.sample,
        end: b.sample,
      })
      nodes[b.node].adjacent.push({
        to: a.node,
        length,
        route: index,
        from: b.sample,
        end: a.sample,
      })
    }
    routes.push(route)
  }

  seaRoutes.forEach((waypoints) => addRoute(waypoints, false))
  landRoutes.forEach((waypoints) => addRoute(waypoints, true))

  // how far every node is from Prague, along the network (Dijkstra)
  const start = node(PRAGUE)
  const distance = nodes.map(() => Infinity)
  const done = nodes.map(() => false)
  distance[start] = 0
  for (;;) {
    let closest = -1
    let closestDistance = Infinity
    nodes.forEach((_, i) => {
      if (!done[i] && distance[i] < closestDistance) {
        closest = i
        closestDistance = distance[i]
      }
    })
    if (closest < 0) break
    done[closest] = true
    for (const edge of nodes[closest].adjacent) {
      distance[edge.to] = Math.min(distance[edge.to], closestDistance + edge.length)
    }
  }

  return { nodes, routes, node, start, distance }
}
